import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  concatMap,
  exhaustMap,
  groupBy,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
} from 'rxjs';
import * as fromGenerated from '../_generated';
import { sentryCaptureError } from '../sentry.helper';
import { AssistantFavoritesActions } from './favorites.actions';

@Injectable()
export class AssistantFavoritesEffects {
  private readonly actions$ = inject(Actions);
  private readonly favoritesService = inject(fromGenerated.FavoritesService);
  private readonly translateService = inject(TranslateService);
  private readonly toastrService = inject(ToastrService);

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantFavoritesActions.load),
      switchMap(() =>
        this.favoritesService
          .favoritesControllerListAssistantFavoritesV1()
          .pipe(
            map((favorites) =>
              AssistantFavoritesActions.loadSuccess({ favorites }),
            ),
            sentryCaptureError,
            catchError((error: Error) =>
              of(
                AssistantFavoritesActions.loadFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  addFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantFavoritesActions.addFavorite),
      groupBy(({ assistantId }) => assistantId),
      mergeMap((group$) =>
        group$.pipe(
          exhaustMap(({ assistantId }) =>
            this.favoritesService
              .favoritesControllerAddAssistantFavoriteV1({ assistantId })
              .pipe(
                map((favorite) =>
                  AssistantFavoritesActions.addFavoriteSuccess({
                    favorite,
                  }),
                ),
                sentryCaptureError,
                catchError((error: Error) =>
                  of(
                    AssistantFavoritesActions.addFavoriteFailure({
                      assistantId,
                      error: error.message,
                    }),
                  ),
                ),
              ),
          ),
        ),
      ),
    ),
  );

  removeFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantFavoritesActions.removeFavorite),
      mergeMap(({ assistantId }) =>
        this.favoritesService
          .favoritesControllerRemoveAssistantFavoriteV1(assistantId)
          .pipe(
            map(() =>
              AssistantFavoritesActions.removeFavoriteSuccess({ assistantId }),
            ),
            sentryCaptureError,
            catchError((error: Error) =>
              of(
                AssistantFavoritesActions.removeFavoriteFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  updateSortOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantFavoritesActions.updateSortOrder),
      concatMap(({ assistantId, sortOrder, previousSortOrder }) =>
        this.favoritesService
          .favoritesControllerUpdateAssistantFavoriteSortOrderV1(assistantId, {
            sortOrder,
          })
          .pipe(
            map(() =>
              AssistantFavoritesActions.updateSortOrderSuccess({
                assistantId,
                sortOrder,
              }),
            ),
            sentryCaptureError,
            catchError((error: Error) =>
              of(
                AssistantFavoritesActions.updateSortOrderFailure({
                  assistantId,
                  attemptedSortOrder: sortOrder,
                  previousSortOrder,
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  showFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AssistantFavoritesActions.loadFailure,
          AssistantFavoritesActions.addFavoriteFailure,
          AssistantFavoritesActions.removeFavoriteFailure,
          AssistantFavoritesActions.updateSortOrderFailure,
        ),
        tap((action) => {
          const translationKey =
            favoriteFailureToastTranslationKeys[action.type];
          this.toastrService.error(
            this.translateService.instant(translationKey) as string,
            undefined,
            { closeButton: true, disableTimeOut: true },
          );
        }),
      ),
    { dispatch: false },
  );
}

const favoriteFailureToastTranslationKeys = {
  [AssistantFavoritesActions.loadFailure.type]: 'TOAST.ERROR.FAVORITES_LOAD',
  [AssistantFavoritesActions.addFavoriteFailure.type]:
    'TOAST.ERROR.FAVORITES_ADD',
  [AssistantFavoritesActions.removeFavoriteFailure.type]:
    'TOAST.ERROR.FAVORITES_REMOVE',
  [AssistantFavoritesActions.updateSortOrderFailure.type]:
    'TOAST.ERROR.FAVORITES_REORDER',
} as const;
